import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanelInlineItem } from '@/ui';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import type { IncidentPsap } from '@/signalr';
import { orderShowFirst, psapIdEquals } from '../model/incidentDomain';
import { InvolvedPsapsDetailDialog, type InvolvedPsapsDetailDialogHandle } from '../dialogs/InvolvedPsapsDetailDialog';
import { InvolvedPsapStateComponent } from './InvolvedPsapStateComponent';
import type { IncidentInvolvedPsapStateComponentInfo } from './involvedPsapsInfo';

export type InvolvedPsapsPanelProps = {
  incidentInvolvedPsapStates: readonly IncidentPsap[];
  createdTimeUtc: string;
  closedTimeUtc: string | null;
  incidentIsReadOnly: boolean;
};

export function InvolvedPsapsPanel({ incidentInvolvedPsapStates }: InvolvedPsapsPanelProps) {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const detailDialogRef = useRef<InvolvedPsapsDetailDialogHandle>(null);

  const involvedPsapStatesWithNames: IncidentInvolvedPsapStateComponentInfo[] = useMemo(
    () =>
      orderShowFirst(incidentInvolvedPsapStates, (psap) => psapIdEquals(psap.psapId, psapConfiguration.id)).map(
        (psap) => ({
          psapId: psap.psapId,
          emergencyServiceType: psap.emergencyServiceType,
          psapName: psap.displayName,
          currentState: { timestampUtc: psap.currentStateSinceUtc, state: psap.currentState },
          statusHistory: psap.statusHistory,
        }),
      ),
    [incidentInvolvedPsapStates, psapConfiguration.id],
  );

  const involvedOtherPsapStatesWithNames = involvedPsapStatesWithNames.filter(
    (psap) => !psapIdEquals(psap.psapId, psapConfiguration.id),
  );

  if (involvedOtherPsapStatesWithNames.length === 0) {
    return null;
  }

  return (
    <>
      <DataPanelInlineItem
        title={t('Incident_InfoPanel_OtherInvolvedEmergencyUnits')}
        clickable
        onPanelClick={() => detailDialogRef.current?.show(involvedPsapStatesWithNames)}
        hoverTooltip={t('Tooltip_ShowDetails')}
      >
        <div className="d-flex gap-1 flex-wrap justify-content-end">
          {involvedOtherPsapStatesWithNames.map((item) => (
            <InvolvedPsapStateComponent
              key={item.psapId}
              collaborationState={item.currentState.state}
              psapName={item.psapName}
            />
          ))}
        </div>
      </DataPanelInlineItem>

      <InvolvedPsapsDetailDialog ref={detailDialogRef} />
    </>
  );
}
